import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea as UITextarea } from "@/components/ui/textarea";
import { Star, Plus, Send, Trash2, HeartHandshake, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { id: "all", en: "All", es: "Todas" },
  { id: "health", en: "Health", es: "Salud" },
  { id: "family", en: "Family", es: "Familia" },
  { id: "guidance", en: "Guidance", es: "Dirección" },
  { id: "thanksgiving", en: "Thanksgiving", es: "Agradecimiento" },
  { id: "provision", en: "Provision", es: "Provisión" },
  { id: "other", en: "Other", es: "Otro" },
];

const T = (obj, lang) => (lang === "es" ? obj.es : obj.en);

export default function Prayers() {
  const { lang, user } = useApp();
  const [prayers, setPrayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [filter, setFilter] = useState("open"); // "open" | "answered" | "all"
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [newCategory, setNewCategory] = useState("other");
  const [anon, setAnon] = useState(false);
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (category !== "all") params.category = category;
      if (filter === "open") params.answered = false;
      if (filter === "answered") params.answered = true;
      const res = await api.get("/prayers", { params });
      setPrayers(res.data);
    } finally {
      setLoading(false);
    }
  }, [category, filter]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      await api.post("/prayers", { text: text.trim(), category: newCategory, is_anonymous: anon });
      setText("");
      setAnon(false);
      setNewCategory("other");
      setOpen(false);
      toast.success(lang === "es" ? "Petición compartida" : "Prayer shared");
      load();
    } finally {
      setPosting(false);
    }
  };

  const togglePray = async (p) => {
    const res = await api.post(`/prayers/${p.prayer_id}/pray`);
    setPrayers((prev) =>
      prev.map((x) =>
        x.prayer_id === p.prayer_id
          ? { ...x, prayed_by_me: res.data.prayed_by_me, prayed_count: res.data.prayed_count }
          : x
      )
    );
    if (res.data.prayed_by_me) {
      toast.success(lang === "es" ? "Oraste por esta petición 🙏" : "You prayed for this 🙏");
    }
  };

  const del = async (p) => {
    if (!window.confirm(lang === "es" ? "¿Eliminar esta petición?" : "Delete this prayer?")) return;
    await api.delete(`/prayers/${p.prayer_id}`);
    toast.success(lang === "es" ? "Petición eliminada" : "Prayer removed");
    load();
  };

  const markAnswered = async (p, testimony) => {
    const res = await api.post(`/prayers/${p.prayer_id}/answer`, { testimony: testimony || "" });
    setPrayers((prev) => prev.map((x) => (x.prayer_id === p.prayer_id ? res.data : x)));
    toast.success(lang === "es" ? "¡Gloria a Dios! Testimonio compartido 🙌" : "Praise God! Testimony shared 🙌");
    if (filter === "open") setTimeout(load, 300);
  };

  const undoAnswer = async (p) => {
    const res = await api.post(`/prayers/${p.prayer_id}/unanswer`);
    setPrayers((prev) => prev.map((x) => (x.prayer_id === p.prayer_id ? res.data : x)));
    if (filter === "answered") setTimeout(load, 300);
  };

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 text-[var(--accent)]">
            <HeartHandshake className="w-4 h-4" strokeWidth={1.5} />
            <span className="uppercase tracking-[0.3em] text-[10px]">
              {lang === "es" ? "Muro de oración" : "Prayer wall"}
            </span>
          </div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight mt-2">
            {lang === "es" ? "Oremos los unos por los otros." : "Let's pray for one another."}
          </h1>
          <p className="mt-3 text-[var(--text-secondary)] text-sm max-w-2xl leading-relaxed">
            {lang === "es"
              ? "Comparte una petición o une tu oración a las de la comunidad. Con dignidad, sin ruido."
              : "Share a prayer request or join the community in prayer. With dignity, in quiet."}
          </p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="new-prayer-btn"
              className="rounded-full h-11 px-5 bg-[var(--accent)] hover:bg-[var(--accent)]/90 text-white"
            >
              <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
              {lang === "es" ? "Nueva petición" : "New prayer"}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[var(--surface-elevated)] border-[var(--border)] max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl tracking-tight">
                {lang === "es" ? "Compartir petición" : "Share a prayer"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <Textarea
                data-testid="prayer-text-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={
                  lang === "es"
                    ? "¿Por qué quieres que la comunidad ore?"
                    : "What would you like the community to pray for?"
                }
                maxLength={600}
                className="min-h-[130px] bg-[var(--bg)] border-[var(--border)]"
              />
              <div className="text-[10px] uppercase tracking-widest text-[var(--text-secondary)] flex justify-between">
                <span>{lang === "es" ? "Categoría" : "Category"}</span>
                <span>{text.length}/600</span>
              </div>
              <Select value={newCategory} onValueChange={setNewCategory}>
                <SelectTrigger
                  data-testid="prayer-category-select"
                  className="h-10 rounded-lg bg-[var(--bg)] border-[var(--border)]"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[var(--surface-elevated)]">
                  {CATEGORIES.filter((c) => c.id !== "all").map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {T(c, lang)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  data-testid="prayer-anonymous"
                  checked={anon}
                  onCheckedChange={setAnon}
                  className="mt-0.5 border-[var(--border)] data-[state=checked]:bg-[var(--accent)] data-[state=checked]:border-[var(--accent)]"
                />
                <div>
                  <p className="text-sm">{lang === "es" ? "Publicar de forma anónima" : "Share anonymously"}</p>
                  <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                    {lang === "es"
                      ? "Tu nombre y foto no aparecerán en esta petición."
                      : "Your name and picture won't appear on this request."}
                  </p>
                </div>
              </label>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full">
                  {lang === "es" ? "Cancelar" : "Cancel"}
                </Button>
                <Button
                  data-testid="prayer-submit"
                  onClick={submit}
                  disabled={posting || !text.trim()}
                  className="rounded-full bg-[var(--accent)] hover:bg-[var(--accent)]/90 text-white"
                >
                  {lang === "es" ? "Compartir" : "Share"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Status filter */}
      <div className="mt-8 flex items-center gap-2">
        {[
          { id: "open", en: "Open", es: "Abiertas" },
          { id: "answered", en: "Answered", es: "Respondidas" },
          { id: "all", en: "All", es: "Todas" },
        ].map((f) => (
          <button
            key={f.id}
            data-testid={`prayer-status-${f.id}`}
            onClick={() => setFilter(f.id)}
            className={`px-4 h-9 rounded-full text-xs uppercase tracking-widest transition-colors duration-200 ${
              filter === f.id
                ? "bg-[var(--accent)] text-white"
                : "border border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--surface)]"
            }`}
          >
            {T(f, lang)}
          </button>
        ))}
      </div>

      {/* Category filter */}
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        {CATEGORIES.map((c) => (
          <button
            key={c.id}
            data-testid={`filter-${c.id}`}
            onClick={() => setCategory(c.id)}
            className={`px-3 h-8 rounded-full text-[11px] uppercase tracking-widest transition-colors duration-200 ${
              category === c.id
                ? "bg-[var(--text-primary)] text-[var(--bg)]"
                : "border border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--surface)]"
            }`}
          >
            {T(c, lang)}
          </button>
        ))}
      </div>

      <div className="mt-8 max-w-2xl space-y-5">
        {loading && <p className="text-[var(--text-secondary)]">{lang === "es" ? "Cargando…" : "Loading…"}</p>}
        {!loading && prayers.length === 0 && (
          <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-elevated)] p-10 text-center">
            <HeartHandshake className="w-8 h-8 mx-auto text-[var(--accent)]" strokeWidth={1.2} />
            <p className="mt-4 text-[var(--text-secondary)]">
              {lang === "es"
                ? "Aún no hay peticiones en esta categoría. Comparte la primera."
                : "No prayers in this category yet. Share the first."}
            </p>
          </div>
        )}
        {prayers.map((p) => (
          <PrayerCard
            key={p.prayer_id}
            prayer={p}
            onPray={togglePray}
            onDelete={del}
            onAnswer={markAnswered}
            onUndoAnswer={undoAnswer}
            lang={lang}
            me={user}
          />
        ))}
      </div>
    </div>
  );
}

function PrayerCard({ prayer, onPray, onDelete, onAnswer, onUndoAnswer, lang, me }) {
  const [replies, setReplies] = useState([]);
  const [openReplies, setOpenReplies] = useState(false);
  const [text, setText] = useState("");
  const [count, setCount] = useState(prayer.replies_count);
  const [answerOpen, setAnswerOpen] = useState(false);
  const [testimony, setTestimony] = useState("");

  const cat = CATEGORIES.find((c) => c.id === prayer.category);

  const loadReplies = async () => {
    const res = await api.get(`/prayers/${prayer.prayer_id}/replies`);
    setReplies(res.data);
  };

  const toggle = async () => {
    const next = !openReplies;
    setOpenReplies(next);
    if (next && replies.length === 0) await loadReplies();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const res = await api.post(`/prayers/${prayer.prayer_id}/replies`, { text: text.trim() });
    setReplies((r) => [...r, res.data]);
    setCount((n) => n + 1);
    setText("");
  };

  return (
    <article
      data-testid={`prayer-${prayer.prayer_id}`}
      className={`rounded-2xl border p-6 ${
        prayer.is_answered
          ? "border-[var(--accent)]/50 bg-[var(--highlight)]/30"
          : "border-[var(--border)] bg-[var(--surface-elevated)]"
      }`}
    >
      {prayer.is_answered && (
        <div className="mb-4 flex items-center gap-2 uppercase tracking-[0.3em] text-[10px] text-[var(--accent)]">
          <Sparkles className="w-3 h-3" strokeWidth={1.5} />
          {lang === "es" ? "Oración respondida" : "Answered prayer"}
        </div>
      )}
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Avatar className="w-9 h-9">
            {!prayer.is_anonymous && <AvatarImage src={prayer.user_picture} alt={prayer.user_name} />}
            <AvatarFallback className="bg-[var(--surface)] text-sm">
              {prayer.is_anonymous ? "?" : (prayer.user_name?.[0]?.toUpperCase() || "U")}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="text-sm flex items-center gap-1.5">
              {prayer.is_anonymous
                ? (lang === "es" ? "Anónimo" : "Anonymous")
                : prayer.user_name}
              {prayer.user_is_supporter && !prayer.is_anonymous && (
                <Star className="w-3 h-3 text-[var(--accent)] fill-[var(--accent)]" strokeWidth={1.5} />
              )}
            </p>
            <p className="text-[10px] uppercase tracking-widest text-[var(--text-secondary)]">
              {new Date(prayer.created_at).toLocaleDateString()}
              {cat && <> · {T(cat, lang)}</>}
            </p>
          </div>
        </div>
        {prayer.is_mine && (
          <button
            data-testid={`delete-prayer-${prayer.prayer_id}`}
            onClick={() => onDelete(prayer)}
            className="text-[var(--text-secondary)] hover:text-[var(--accent)] transition-colors duration-200 p-2"
          >
            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
          </button>
        )}
      </header>

      <p className="mt-5 leading-relaxed text-[var(--text-primary)] font-serif-body text-lg">
        {prayer.text}
      </p>

      {prayer.is_answered && prayer.testimony && (
        <div className="mt-5 pl-4 border-l-2 border-[var(--accent)]">
          <p className="uppercase tracking-[0.25em] text-[10px] text-[var(--accent)] mb-2">
            {lang === "es" ? "Testimonio" : "Testimony"}
          </p>
          <p className="font-serif-body text-base leading-relaxed text-[var(--text-primary)]">
            {prayer.testimony}
          </p>
          {prayer.answered_at && (
            <p className="mt-2 text-[10px] uppercase tracking-widest text-[var(--text-secondary)]">
              {new Date(prayer.answered_at).toLocaleDateString()}
            </p>
          )}
        </div>
      )}

      <div className="mt-5 flex items-center gap-6 text-xs uppercase tracking-widest text-[var(--text-secondary)] flex-wrap">
        <button
          data-testid={`pray-btn-${prayer.prayer_id}`}
          onClick={() => onPray(prayer)}
          className={`flex items-center gap-2 transition-colors duration-200 ${
            prayer.prayed_by_me ? "text-[var(--accent)]" : "hover:text-[var(--accent)]"
          }`}
        >
          <PrayIcon filled={prayer.prayed_by_me} />
          <span>
            {prayer.prayed_count}{" "}
            {lang === "es"
              ? (prayer.prayed_count === 1 ? "oración" : "oraciones")
              : (prayer.prayed_count === 1 ? "prayed" : "prayed")}
          </span>
        </button>
        <button
          data-testid={`replies-toggle-${prayer.prayer_id}`}
          onClick={toggle}
          className="flex items-center gap-2 hover:text-[var(--accent)] transition-colors duration-200"
        >
          <span>
            {count} {lang === "es" ? "palabras" : "words"}
          </span>
        </button>
        {prayer.is_mine && !prayer.is_answered && (
          <button
            data-testid={`mark-answered-btn-${prayer.prayer_id}`}
            onClick={() => setAnswerOpen(true)}
            className="ml-auto flex items-center gap-2 text-[var(--accent)] hover:opacity-80 transition-opacity duration-200"
          >
            <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} />
            <span>{lang === "es" ? "Marcar respondida" : "Mark answered"}</span>
          </button>
        )}
        {prayer.is_mine && prayer.is_answered && (
          <button
            data-testid={`undo-answered-btn-${prayer.prayer_id}`}
            onClick={() => onUndoAnswer(prayer)}
            className="ml-auto hover:text-[var(--accent)] transition-colors duration-200"
          >
            <span>{lang === "es" ? "Deshacer" : "Undo"}</span>
          </button>
        )}
      </div>

      {/* Answer testimony dialog */}
      <Dialog open={answerOpen} onOpenChange={setAnswerOpen}>
        <DialogContent
          data-testid={`answer-dialog-${prayer.prayer_id}`}
          className="bg-[var(--surface-elevated)] border-[var(--border)] max-w-md"
        >
          <DialogHeader>
            <div className="flex items-center gap-2 text-[var(--accent)]">
              <Sparkles className="w-4 h-4" strokeWidth={1.5} />
              <span className="uppercase tracking-[0.3em] text-[10px]">
                {lang === "es" ? "Oración respondida" : "Prayer answered"}
              </span>
            </div>
            <DialogTitle className="font-serif text-2xl tracking-tight">
              {lang === "es" ? "Comparte tu testimonio" : "Share your testimony"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
            {lang === "es"
              ? "Cuéntale a los que oraron por ti cómo Dios respondió. Un mensaje breve alienta a toda la comunidad."
              : "Tell the community who prayed for you how God answered. A short note encourages everyone."}
          </p>
          <UITextarea
            data-testid={`testimony-input-${prayer.prayer_id}`}
            value={testimony}
            onChange={(e) => setTestimony(e.target.value)}
            placeholder={lang === "es" ? "Ej: Dios abrió una puerta que parecía cerrada…" : "e.g. God opened a door that seemed closed…"}
            maxLength={500}
            className="min-h-[120px] bg-[var(--bg)] border-[var(--border)]"
          />
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-widest text-[var(--text-secondary)]">
              {testimony.length}/500 · {lang === "es" ? "opcional" : "optional"}
            </span>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setAnswerOpen(false)} className="rounded-full">
                {lang === "es" ? "Cancelar" : "Cancel"}
              </Button>
              <Button
                data-testid={`answer-submit-${prayer.prayer_id}`}
                onClick={async () => {
                  await onAnswer(prayer, testimony);
                  setAnswerOpen(false);
                  setTestimony("");
                }}
                className="rounded-full bg-[var(--accent)] hover:bg-[var(--accent)]/90 text-white"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                {lang === "es" ? "Marcar respondida" : "Mark as answered"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {openReplies && (
        <div className="mt-5 pt-5 border-t border-[var(--border)] space-y-4">
          {replies.map((r) => (
            <div key={r.reply_id} className="flex items-start gap-3">
              <Avatar className="w-7 h-7">
                <AvatarImage src={r.user_picture} alt={r.user_name} />
                <AvatarFallback className="text-xs bg-[var(--surface)]">
                  {r.user_name?.[0]?.toUpperCase() || "?"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <p className="text-xs text-[var(--text-secondary)] flex items-center gap-1">
                  {r.user_name}
                  {r.user_is_supporter && (
                    <Star className="w-3 h-3 text-[var(--accent)] fill-[var(--accent)]" strokeWidth={1.5} />
                  )}
                </p>
                <p className="text-sm mt-0.5 font-serif-body leading-relaxed">{r.text}</p>
              </div>
            </div>
          ))}
          <form onSubmit={submit} className="flex items-center gap-2">
            <Input
              data-testid={`reply-input-${prayer.prayer_id}`}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={
                lang === "es" ? "Palabra de aliento…" : "A word of encouragement…"
              }
              className="h-10 rounded-full bg-[var(--bg)] border-[var(--border)]"
            />
            <button
              data-testid={`reply-submit-${prayer.prayer_id}`}
              type="submit"
              className="h-10 w-10 rounded-full flex items-center justify-center bg-[var(--accent)] text-white hover:opacity-90 transition-opacity duration-200"
            >
              <Send className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </form>
        </div>
      )}
    </article>
  );
}

// Custom hands-in-prayer icon (folded hands)
function PrayIcon({ filled }) {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 3v9l-3 2c-1 .7-2 2-2 3.5V21h6v-3l3-2c1-.7 2-2 2-3.5V4a1 1 0 0 0-2 0v6" />
      <path d="M11 3a1 1 0 0 0-2 0v9" />
    </svg>
  );
}
